import { useState } from "preact/hooks";
import { AttachmentSlot } from "../../../api/types";
import type { AttachmentId, AttachmentInfo, FullWeaponInfo } from "../../../api/types";
import { LockBadge } from "../../components/Badge";
import { OptionCard } from "../../components/OptionCard";
import { cx } from "../../cx";
import { MAX_ATTACHMENTS } from "./draft";
import { ATTACHMENT_SLOTS, ATTACHMENT_SLOT_LABEL } from "./labels";

interface AttachmentOption {
  info: AttachmentInfo;
  /** Weapon level that unlocks it. */
  level: number;
}

export interface AttachmentPickerProps {
  weapon: FullWeaponInfo;
  chosen: readonly AttachmentId[];
  /** Attachment ids unlocked for this weapon. */
  unlocked: readonly AttachmentId[];
  /** The player's current level with this weapon. */
  weaponLevel: number;
  catalog: ReadonlyMap<AttachmentId, AttachmentInfo>;
  onToggle: (id: AttachmentId) => void;
}

/** Slot rows of toggle chips plus a details box for the hovered, focused or last chosen attachment. */
export function AttachmentPicker({ weapon, chosen, unlocked, weaponLevel, catalog, onToggle }: AttachmentPickerProps) {
  const [hover, setHover] = useState<AttachmentId | null>(null);
  const [focus, setFocus] = useState<AttachmentId | null>(null);

  const bySlot: Record<AttachmentSlot, AttachmentOption[]> = {
    [AttachmentSlot.OPTIC]: [],
    [AttachmentSlot.MUZZLE]: [],
    [AttachmentSlot.BARREL]: [],
    [AttachmentSlot.MAGAZINE]: [],
    [AttachmentSlot.GRIP]: [],
  };
  weapon.allowedAttachments.forEach((id, i) => {
    const info = catalog.get(id);
    if (info) bySlot[info.slot].push({ info, level: weapon.attachmentLevels[i] });
  });

  const occupied = new Set<AttachmentSlot>();
  for (const id of chosen) {
    const slot = catalog.get(id)?.slot;
    if (slot !== undefined) occupied.add(slot);
  }
  const full = chosen.length >= MAX_ATTACHMENTS;

  const inspected = catalog.get(hover ?? focus ?? chosen[chosen.length - 1]);

  return (
    <div class="loadouts__atts">
      {ATTACHMENT_SLOTS.map((slot) => (
        <div key={slot} class="loadouts__slot" role="group" aria-label={ATTACHMENT_SLOT_LABEL[slot]}>
          <span class="loadouts__slot-label" aria-hidden="true">
            {ATTACHMENT_SLOT_LABEL[slot]}
          </span>
          <div class="loadouts__chips">
            {bySlot[slot].length === 0 && <span class="loadouts__none">Not available for this weapon</span>}
            {bySlot[slot].map(({ info, level }) => {
              const isChosen = chosen.includes(info.id);
              const locked = !unlocked.includes(info.id);
              // A full loadout still allows swapping within an occupied slot.
              const capped = !isChosen && full && !occupied.has(slot);
              let title = info.description;
              if (locked) title = `Requires weapon level ${level} (yours: ${weaponLevel}). ${info.description}`;
              else if (capped) title = `Maximum of ${MAX_ATTACHMENTS} attachments: remove one first.`;
              return (
                <div
                  key={info.id}
                  class="loadouts__chipwrap"
                  onMouseEnter={() => setHover(info.id)}
                  onMouseLeave={() => setHover(null)}
                  onFocusIn={() => setFocus(info.id)}
                  onFocusOut={() => setFocus(null)}
                >
                  <OptionCard
                    role="checkbox"
                    class={cx("loadouts__chip", capped && "is-capped")}
                    selected={isChosen}
                    locked={locked || capped}
                    title={title}
                    onClick={() => onToggle(info.id)}
                  >
                    <span class="loadouts__chip-name">{info.name}</span>
                    {locked && <LockBadge weapon level={level} class="loadouts__chip-lock" />}
                  </OptionCard>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <div class="loadouts__detail" aria-live="polite">
        {inspected ? (
          <>
            <span class="loadouts__detail-name">{inspected.name}</span>
            <span class="loadouts__detail-text">{inspected.description}</span>
            <ul class="loadouts__effects">
              {inspected.effects.map((effect) => (
                <li key={effect} class="loadouts__effect">
                  {effect}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <span class="loadouts__detail-text muted">Hover, focus or choose an attachment to see its effects.</span>
        )}
      </div>
    </div>
  );
}
